import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Warehouse, Camera, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicWarehouseEvidence() {
  const { token } = useParams();
  const c = new URLSearchParams(window.location.search).get("c");
  const params = c ? { c } : {};

  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ type: "salida", product_id: "", quantity: "", submitted_by: "", notes: "" });
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);

  useEffect(() => {
    axios.get(`${API}/public/warehouse-evidence/${token}`, { params })
      .then(({ data }) => { setInfo(data); })
      .catch((e) => setError(e.response?.data?.detail || "Link inválido o revocado"));
    // eslint-disable-next-line
  }, [token]);

  const onFiles = (e) => {
    const fl = [...e.target.files].slice(0, 6);
    setFiles(fl);
    setPreviews(fl.map((f) => URL.createObjectURL(f)));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("type", form.type);
      fd.append("product_id", form.product_id);
      fd.append("quantity", form.quantity);
      fd.append("submitted_by", form.submitted_by);
      if (form.notes) fd.append("notes", form.notes);
      files.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/public/warehouse-evidence/${token}`, fd, { params });
      setSent(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Error al enviar. Intenta de nuevo.");
    }
    setSending(false);
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg, #060a14)" }}>
        <div className="panel p-6 max-w-sm text-center">
          <p className="text-sm text-slate-300" data-testid="public-evidence-error">{error}</p>
        </div>
      </div>
    );
  }
  if (!info) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg, #060a14)" }}><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg, #060a14)" }}>
        <div className="panel p-8 max-w-sm text-center space-y-3" data-testid="public-evidence-success">
          <CheckCircle2 className="w-12 h-12 mx-auto" style={{ color: "var(--green)" }} />
          <h2 className="text-lg font-bold text-slate-100">Evidencia enviada</h2>
          <p className="text-sm text-slate-400">El responsable del almacén la revisará y validará. Gracias.</p>
          <button data-testid="pub-send-another-btn" onClick={() => { setSent(false); setForm({ ...form, quantity: "", notes: "" }); setFiles([]); setPreviews([]); }} className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Enviar otra</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-6" style={{ background: "var(--bg, #060a14)" }}>
      <div className="max-w-md mx-auto space-y-4">
        <div className="flex items-center gap-3 pt-2">
          <Warehouse className="w-7 h-7" style={{ color: "var(--cyan)" }} />
          <div>
            <h1 className="text-xl font-bold text-slate-100" data-testid="public-evidence-title">{info.warehouse_name}</h1>
            <p className="text-xs text-slate-400">Registro de material · tu registro será validado por el residente</p>
          </div>
        </div>
        <form onSubmit={submit} className="panel p-4 space-y-4" data-testid="public-evidence-form">
          <div className="grid grid-cols-2 gap-2">
            {["salida", "entrada"].map((t) => (
              <button key={t} type="button" data-testid={`pub-type-${t}`} onClick={() => setForm({ ...form, type: t })}
                className="px-3 py-2.5 text-xs uppercase tracking-widest font-semibold rounded-sm"
                style={{ background: form.type === t ? "rgba(34,211,238,0.12)" : "transparent", color: form.type === t ? "var(--cyan)" : "var(--text-dim)", border: `1px solid ${form.type === t ? "var(--cyan)" : "var(--border)"}` }}>
                {t === "salida" ? "Salida / consumo" : "Entrada / llegada"}
              </button>
            ))}
          </div>
          <div>
            <label className="label-mono block mb-1.5">Material</label>
            <select data-testid="pub-product" required value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} className="w-full input-cyber px-3 py-2.5 text-sm">
              <option value="" style={{ background: "#0a0f1d" }}>— Selecciona el material —</option>
              {info.products.map((p) => <option key={p.id} value={p.id} style={{ background: "#0a0f1d" }}>{p.code} · {p.name} ({p.unit})</option>)}
            </select>
          </div>
          <div>
            <label className="label-mono block mb-1.5">Cantidad</label>
            <input data-testid="pub-quantity" required type="number" step="any" min="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="w-full input-cyber px-3 py-2.5 text-sm" />
          </div>
          <div>
            <label className="label-mono block mb-1.5">Tu nombre</label>
            <input data-testid="pub-name" required value={form.submitted_by} onChange={(e) => setForm({ ...form, submitted_by: e.target.value })} className="w-full input-cyber px-3 py-2.5 text-sm" placeholder="Nombre de quien reporta" />
          </div>
          <div>
            <label className="label-mono block mb-1.5 flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> Fotos (hasta 6)</label>
            <input data-testid="pub-photos" type="file" accept="image/*" capture="environment" multiple onChange={onFiles} className="text-xs text-slate-400 w-full" />
            {previews.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {previews.map((src, i) => <img key={i} src={src} alt="" className="aspect-square object-cover rounded-sm" />)}
              </div>
            )}
          </div>
          <div>
            <label className="label-mono block mb-1.5">Notas</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full input-cyber px-3 py-2 text-sm" placeholder="Ej. llegó el camión de grava" />
          </div>
          <button type="submit" disabled={sending} data-testid="pub-submit-btn" className="btn-primary w-full py-3 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50 flex items-center justify-center gap-2">
            {sending && <Loader2 className="w-4 h-4 animate-spin" />} Enviar evidencia
          </button>
        </form>
        <p className="text-[11px] text-center text-slate-500 pb-6">Este formulario no muestra precios ni información financiera.</p>
      </div>
    </div>
  );
}
